import { List, fromJS } from 'immutable';
import get from 'transmute/get';
import { getId, getProperty, setProperty } from 'customer-data-objects/model/ImmutableModel';
import QuoteContact from 'customer-data-objects/quote/QuoteContactRecord';
import QuoteCompany from 'customer-data-objects/quote/QuoteCompanyRecord';
import { BOOLEAN_PROPERTIES, COMPANY_RECORD_TO_SENDER_COMPANY_MAP, DEFAULT_QUOTE_ASSOCIATED_OBJECTS, INTEGER_PROPERTIES, QUOTE_ASSOCIATIONS } from 'customer-data-objects/quote/constants/properties';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
export function getHasDeal(quote) {
  // quote.associations.QUOTE_TO_DEAL
  const isDealIdInAssocationsArray = !!getQuoteAssociation(quote, QUOTE_ASSOCIATIONS.QUOTE_TO_DEAL).first();

  // quote.quote.associatedObjects.QUOTE_TO_DEAL (might be stored here after crm-search)
  const isDealOnQuoteAssociatedObject = !!quote.getIn(['quote', 'associatedObjects', QUOTE_ASSOCIATIONS.QUOTE_TO_DEAL]);
  return isDealIdInAssocationsArray || isDealOnQuoteAssociatedObject;
}
export function inboundPropertiesToQuoteObject(inboundDbRecord, propertyMapper, constructor) {
  const defaultProperties = new constructor();
  const isList = List.isList(propertyMapper);
  return propertyMapper.reduce((quoteProperties, value, key) => {
    const propertyKey = isList ? value : key;
    const inboundPropertyValue = getProperty(inboundDbRecord, value);
    return quoteProperties.set(propertyKey, inboundPropertyValue === undefined ? defaultProperties.get(propertyKey) : inboundPropertyValue);
  }, defaultProperties);
}
export function getQuoteCompany(CRMCompany) {
  return inboundPropertiesToQuoteObject(CRMCompany, COMPANY_RECORD_TO_SENDER_COMPANY_MAP.keySeq().toList(), QuoteCompany).set('companyId', String(getId(CRMCompany)));
}
export function getQuoteSenderContact(quote) {
  // Returns a QuoteContact record by extracting the inboundDB sender contact
  // properties from a quote
  return QuoteContact({
    firstname: getQuoteProperty(quote, 'hs_sender_firstname') || '',
    lastname: getQuoteProperty(quote, 'hs_sender_lastname') || '',
    email: getQuoteProperty(quote, 'hs_sender_email') || '',
    phone: getQuoteProperty(quote, 'hs_sender_phone') || '',
    jobtitle: getQuoteProperty(quote, 'hs_sender_jobtitle') || ''
  });
}
export const PDF_GENERATION_PENDING = 'NO_PDF_FOUND_ERROR';
export function pdfGenerationPendingAlert() {
  return FloatingAlertStore.addAlert({
    type: 'warning',
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "quotesUiLib.pdfGenerationPendingAlert.title"
    }),
    message: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "quotesUiLib.pdfGenerationPendingAlert.text"
    })
  });
}
export function unreachableCase(value) {
  throw new Error(`Didn't expect to get here. Switch value: ${value}`);
}
export function getQuoteId(quoteRecord) {
  return getId(quoteRecord.quote);
}
export function getDealId(quoteRecord) {
  return quoteRecord.associations[QUOTE_ASSOCIATIONS.QUOTE_TO_DEAL].first();
}
export function getQuoteProperty(quoteRecord, property) {
  const value = Object.prototype.hasOwnProperty.call(DEFAULT_QUOTE_ASSOCIATED_OBJECTS, property) ? get(property, quoteRecord.quoteAssociatedObjects) : getProperty(quoteRecord.quote, property);
  if (INTEGER_PROPERTIES.has(property)) {
    return value || value === 0 ? parseInt(value, 10) : null;
  } else if (BOOLEAN_PROPERTIES.has(property)) {
    return value === 'true' || value === true;
  }
  return value;
}
export function getQuoteAssociation(quoteRecord, associationType) {
  return quoteRecord.getIn(['associations', associationType]);
}
export function setQuoteAssociations(quoteRecord, associations) {
  return quoteRecord.update('associations', currentAssociations => currentAssociations.merge(associations));
}
export function setQuoteProperty(quoteRecord, propertyPath, value, source, sourceId) {
  // propertyPath can be a String or Array - to set nested properties within the
  // quoteAssociatedObjects object
  const isArray = Array.isArray(propertyPath);
  const topLevelProperty = isArray ? get(0, propertyPath) : propertyPath;
  if (Object.prototype.hasOwnProperty.call(DEFAULT_QUOTE_ASSOCIATED_OBJECTS, topLevelProperty)) {
    const additionalPropertiesUpdatePath = isArray ? ['quoteAssociatedObjects'].concat(propertyPath) : ['quoteAssociatedObjects', propertyPath];
    return quoteRecord.setIn(additionalPropertiesUpdatePath, value);
  }
  if (isArray) {
    throw new Error('Use a string for propertyPath to set quote inboundDB properties');
  }
  return quoteRecord.update('quote', quote => setProperty(quote, propertyPath, value, source, sourceId));
}
export function mergeQuoteProperties(quoteRecord, updates) {
  const updatesMap = fromJS(updates);
  const inboundDbUpdates = updatesMap.filterNot((value, key) => Object.prototype.hasOwnProperty.call(DEFAULT_QUOTE_ASSOCIATED_OBJECTS, key));
  const additionalPropertyUpdates = updatesMap.filter((value, key) => Object.prototype.hasOwnProperty.call(DEFAULT_QUOTE_ASSOCIATED_OBJECTS, key));
  const updatedquote = inboundDbUpdates.reduce((quote, updatedValue, key) => {
    return setProperty(quote, key, updatedValue);
  }, quoteRecord.quote);
  const updatedQuoteAdditionalProperties = quoteRecord.quoteAssociatedObjects.merge(additionalPropertyUpdates);
  return quoteRecord.merge({
    quote: updatedquote,
    quoteAssociatedObjects: updatedQuoteAdditionalProperties
  });
}